/**
 * One engine, two apps (DukaanBook-Design.docx, section 2).
 *
 * Every business is a pharmacy or a shop, fixed when it signs up. FarmashiBook
 * is the pharmacy app and DukaanBook the shop app; both talk to the same
 * server, and each says which it is with every request. A business opens only
 * in its own app.
 */

export type BusinessKind = "pharmacy" | "shop";

/** The header every request carries: which app sent it. */
export const APP_HEADER = "X-App";

/** What each app sends in `X-App`, by the kind of business it serves. */
export const APP_NAMES: Record<BusinessKind, string> = {
  pharmacy: "FarmashiBook",
  shop: "DukaanBook",
};

/**
 * The kind of business an app serves, from its `X-App` header.
 *
 * No header is FarmashiBook: every app from before DukaanBook existed sends
 * none, and those phones must keep working. A name that is neither app is
 * null — the caller refuses it rather than guess.
 */
export function kindOfApp(header: string | null | undefined): BusinessKind | null {
  const name = header?.trim();
  if (!name) return "pharmacy";
  const lower = name.toLowerCase();
  for (const kind of Object.keys(APP_NAMES) as BusinessKind[]) {
    if (APP_NAMES[kind].toLowerCase() === lower) return kind;
  }
  return null;
}

/**
 * The feature list per type (section 2.2, as redesigned on 2026-10-27). Kept
 * here so the server and the apps read the same list: the server refuses what
 * a type does not have, the app does not show it.
 *
 * Both types share products (with packs), sales, credit, branches and
 * transfers, reports, staff, settings and plans. Expiry is not here because
 * both have it: on by default for a pharmacy, off for a shop (see
 * `expiryOnByDefault`).
 */
export const FEATURES = {
  /** Strength, form, generic name, prescription flag. */
  medicineFields: { pharmacy: true, shop: false },
  /** The shared list of medicines to start a product from. Shops start empty. */
  medicineList: { pharmacy: true, shop: false },
  /** Selling by money: "$1 of sugar" (2026-10-27). */
  sellByMoney: { pharmacy: false, shop: true },
  /** Credit the business owes its suppliers: later, for neither yet. */
  supplierCredit: { pharmacy: false, shop: false },
} as const satisfies Record<string, Record<BusinessKind, boolean>>;

export type Feature = keyof typeof FEATURES;

/** Whether a business of this kind has this feature. */
export function hasFeature(kind: BusinessKind, feature: Feature): boolean {
  return FEATURES[feature][kind];
}

/** Whether a new product asks for an expiry date unless switched off (decision D7). */
export function expiryOnByDefault(kind: BusinessKind): boolean {
  return kind === "pharmacy";
}
